import { PropsWithChildren } from "react";
import clsx from "clsx";
import styled from "styled-components";

import { FormStates, StateValue } from "@/panels/signup/FormStateMachine";
import { useSignupStore } from "@/reducers";
import classes from "./stepper.module.css";

const isAccountStep = (state: StateValue) => {
  return [
    //FormStates.KYC,
    FormStates.EMAIL,
    FormStates.PHONE,
    FormStates.VERIFY_PHONE,
    FormStates.VERIFY_EMAIL,
    FormStates.DISCLOSURE,
  ].includes(state);
};

const isVerificationStep = (state: StateValue) => {
  return [FormStates.KYC_GENERATE_QUESTIONS, FormStates.PASSWORD].includes(
    state
  );
};

const isPersonalStep = (state: StateValue) => {
  return [FormStates.PERSONAL_INFO].includes(state);
};

const isOccupationStep = (state: StateValue) => {
  return [FormStates.OCCUPATION].includes(state);
};

const List = styled.ol`
  display: flex;
  flex-direction: column;
`;

const Item = ({
  isActive,
  children,
  ariaLabel,
  testId,
}: PropsWithChildren<{ isActive: boolean; ariaLabel?: string; testId?: string }>) => {
  return (
    <li
      aria-label={ariaLabel}
      data-testid={testId}
      className={clsx(
        classes.item,
        "after:border-2 before:bg-pebble dark:before:bg-rock",
        {
          ["text-clementine dark:text-sun after:bg-clementine dark:after:bg-sun after:border-clementine dark:after:border-sun"]:
            isActive,
          "after:border-pebble dark:after:border-rock after:bg-pebble dark:after:bg-rock text-pebble dark:text-rock":
            !isActive,
        }
      )}
    >
      {children}
    </li>
  );
};

const Step = ({
  isActive,
  children,
  testId,
}: PropsWithChildren<{ isActive: boolean; testId?: string }>) => {
  return (
    <li
      data-testid={testId}
      className={clsx(classes.step, {
        "text-clementine dark:text-sun": isActive,
        "text-pebble dark:text-rock": !isActive,
      })}
    >
      {children}
    </li>
  );
};

export const Stepper = () => {
  const formState = useSignupStore.use.formState();

  if (formState === FormStates.EMAIL) {
    return (
      <div className="hidden md:block" data-testid="container-signup-stepper">
        <div className="dark:text-sun text-clementine text-md md:text-[48px] leading-tight" data-testid="text-signup-stepper-title">
          Sign up to Paytrie,
        </div>
        <div className="text-md md:text-[48px] leading-tight">
          Canada&apos;s home for stablecoins
        </div>
      </div>
    );
  }

  return (
    <List className="mt-1 px-3" data-testid="container-signup-stepper">
      <Item isActive={isAccountStep(formState)} ariaLabel="Account" testId="item-signup-stepper-account">
        <h3 className={classes.stepTitle} data-testid="text-signup-stepper-title">Account</h3>
        {isAccountStep(formState) && (
          <ul data-testid="list-signup-stepper">
            <Step
              isActive={[FormStates.VERIFY_EMAIL, FormStates.EMAIL].includes(
                formState
              )}
              testId="step-signup-stepper-email"
            >
              Email
            </Step>
            <Step
              isActive={[FormStates.PHONE, FormStates.VERIFY_PHONE].includes(
                formState
              )}
              testId="step-signup-stepper-phone"
            >
              Phone number
            </Step>
            <Step 
              isActive={[FormStates.DISCLOSURE].includes(formState)}
              testId="step-signup-stepper-disclosure"
            >
              Disclosure
            </Step>
          </ul>
        )}
      </Item>
      <Item isActive={isPersonalStep(formState)} testId="item-signup-stepper-identity">
        <h3 className={classes.stepTitle} data-testid="text-signup-stepper-title">Identity</h3>
      </Item>
      <Item isActive={isOccupationStep(formState)} testId="item-signup-stepper-occupation">
        <h3 className={classes.stepTitle} data-testid="text-signup-stepper-title">Occupation</h3>
      </Item>
      <Item isActive={isVerificationStep(formState)} testId="item-signup-stepper-verification">
        <h3 className={classes.stepTitle} data-testid="text-signup-stepper-title">Verification</h3>
        {isVerificationStep(formState) &&
          formState === FormStates.KYC_GENERATE_QUESTIONS && <div />}
      </Item>
    </List>
  );
};

// Suggestions
// 1. Consolidate the isXXXStep functions into a single function with an enum parameter to reduce repetition
// 2. Consider using an enum or constants object instead of strings for step identifiers
// 3. Implement a configuration-driven approach for rendering steps to make the component more maintainable
// 4. Add animations for step transitions to improve user experience
// 5. Extract the step logic into a custom hook for better separation of concerns
